import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BottomSheetRef } from 'react-spring-bottom-sheet';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Icon } from '~/components/atomic';
import { useModal } from '~/hooks';
import { ModalParams } from '~/types';
import { MissionAnswerType } from '~/types/missionGame';
import { Mission } from '~/types/missionGameApi';
import { GAEvent } from '~/utils';
import { GA } from '~/constants';
import { useUserStore } from '~/store';
import useMissionGameStore from '~/store/useMissionGameStore';
import BottomSheetContainer from '../BottomSheet';
import MissionItem, { MissionText } from './MissionItem';

const MissionBottomSheet = ({
  missions,
  gameId,
  gameTitle,
}: {
  missions: Mission[];
  gameId: string;
  gameTitle: string;
}) => {
  const bottomSheetRef = useRef<BottomSheetRef>(null);

  const modalData: ModalParams = {
    title: 'Ready to See the Ending?',
    content: 'To continue, Click Go Ending.',
    click: () => handleMissionSubmit(),
    buttonName: 'Go Ending',
    type: 'double_emphasize',
  };

  const navigate = useNavigate();
  const { openModal } = useModal();
  const email = useUserStore((state) => state.email);
  const { answerContext, setAnswerContext } = useMissionGameStore(
    (state) => state,
  );
  const [answer, setAnswer] = useState<MissionAnswerType[]>([]);
  const [currAnsweringIdx, setCurrAnsweringIdx] = useState<number | null>(null);

  const handleAnswerInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const questionId = missions[currAnsweringIdx!].question_id;
    const answer = e.target.value;

    answer.length <= 100 &&
      setAnswer((prev) =>
        [
          { question_id: questionId.toString(), answer },
          ...prev.filter((item) => item.question_id !== questionId.toString()),
        ].sort((a, b) => Number(a.question_id) - Number(b.question_id)),
      );
  };

  const handleMissionSubmit = () => {
    // send GA event : CLICK_GO_ENDING
    GAEvent(email, GA.ACTION.CLICK_GO_ENDING, {
      game_id: gameId,
      game_title: gameTitle,
    });

    const answerSubmitRequestData = {
      game_id: Number(gameId),
      question_id_1: Number(answer[0].question_id),
      answer_q1: answer[0].answer,
      question_id_2: Number(answer[1].question_id),
      answer_q2: answer[1].answer,
      question_id_3: Number(answer[2].question_id),
      answer_q3: answer[2].answer,
    };

    navigate('/mission/result', {
      state: { answers: answerSubmitRequestData },
    });
  };

  const saveToAnswerContext = () => {
    const sortedAnswer = [...answer].sort(
      (a, b) => Number(a.question_id ?? 0) - Number(b.question_id ?? 0),
    );
    setAnswerContext(sortedAnswer);
  };

  const snapBottomSheetToMax = () => {
    bottomSheetRef.current?.snapTo(({ maxHeight }) => maxHeight);
  };

  useEffect(() => {
    if (!missions.length || answer.length > 0) return;

    // 미션 답변 초기 세팅
    setAnswer(
      missions
        .map((mission) => {
          const initialAnswer = answerContext.find(
            (ans) => Number(ans.question_id) === mission.question_id,
          )?.answer;

          return {
            question_id: mission.question_id.toString(),
            answer: initialAnswer || '',
          };
        })
        .sort((a, b) => Number(a.question_id) - Number(b.question_id)),
    );
  }, [missions, answerContext]);

  return (
    <Container>
      <CheckMissionSection onClick={snapBottomSheetToMax}>
        <span>Check mission</span>
        <Icon iconName='ArrowDown' size={20} color='content_light_1' />
      </CheckMissionSection>
      <BottomSheetContainer
        ref={bottomSheetRef}
        isOpen={true}
        openHeightRates={[0.93, 0.04]}
        footerChildren={
          <Button
            onClick={
              currAnsweringIdx !== null
                ? () => {
                    saveToAnswerContext();
                    setCurrAnsweringIdx(null);
                  }
                : () => {
                    // send GA event : CLICK_SUBMIT_ANSWER
                    GAEvent(email, GA.ACTION.CLICK_SUBMIT_ANSWER, {
                      game_id: gameId,
                      game_title: gameTitle,
                    });
                    openModal(modalData);
                  }
            }
          >
            {currAnsweringIdx !== null ? 'Save' : 'Submit'}
          </Button>
        }
      >
        {/* 미션 바텀시트 컨텐츠 */}
        <BottomSheetContent>
          {currAnsweringIdx === null ? (
            <>
              <TextContainer>
                <Title>Mission</Title>
                <Description>
                  Find the truth and answer the question.
                </Description>
              </TextContainer>

              {missions.map((mission, idx) => (
                <MissionItem
                  key={mission.question_id}
                  mission={mission}
                  missionNum={idx + 1}
                  answer={
                    answer.find(
                      (item) =>
                        item.question_id === mission.question_id.toString(),
                    )?.answer ?? ''
                  }
                  onAnswerClick={() => setCurrAnsweringIdx(idx)}
                />
              ))}
            </>
          ) : (
            <AnswerContainer>
              <MissionText
                missionNum={currAnsweringIdx + 1}
                question={missions[currAnsweringIdx].question || ''}
              />
              <div style={{ position: 'relative' }}>
                <AnswerInput
                  placeholder='Answer'
                  value={answer[currAnsweringIdx].answer}
                  onChange={handleAnswerInput}
                />
                <AnswerLengthText>{`${answer[currAnsweringIdx].answer.length}/100`}</AnswerLengthText>
              </div>
            </AnswerContainer>
          )}
        </BottomSheetContent>
      </BottomSheetContainer>
    </Container>
  );
};

export default MissionBottomSheet;

const Container = styled.div`
  position: absolute;
  height: 100%;
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  text-align: center;
  gap: 5px;
`;

const Title = styled.div`
  font-size: 24px;
  font-weight: 500;
`;

const Description = styled.div`
  color: ${({ theme }) => theme.content.content_light_4};
  font-size: 14px;
  font-weight: 400;
`;

const BottomSheetContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  overflow: hidden;
  gap: 40px;
`;

const AnswerInput = styled.textarea`
  width: 100%;
  min-height: 130px;
  max-height: 250px;
  padding: 15px;
  border: 1px solid ${({ theme }) => theme.content.content_light_5};
  border-radius: 10px;
  color: ${({ theme }) => theme.content.content_light_1};
  font-size: 16px;
  resize: none;
`;

const AnswerContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 20px;
`;

const Button = styled.button`
  width: 100%;
  height: 44px;
  margin-bottom: 8px;
  padding: 12px;
  border-radius: 22px;
  background-color: ${({ theme }) => theme.backgroundColor.bg_light_1};
  color: ${({ theme }) => theme.content.content_dark_1};
  font-size: 18px;
  font-weight: 500;
`;

const AnswerLengthText = styled.span`
  position: absolute;
  right: 15px;
  bottom: 15px;
  color: ${({ theme }) => theme.content.content_light_4};
  font-size: 14px;
`;

const CheckMissionSection = styled.div`
  display: flex;
  position: absolute;
  bottom: 4%;
  left: 50%;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transform: translate(-50%, 0);
  font-size: 18px;
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;
  gap: 2px;
  ${({ theme }) => css`
    color: ${theme.content.content_light_1};
  `};
`;
